import { isBrReminderEnabled } from './brConfig';
import {
  fetchKybAccessGate,
  isKybLockPeriodActiveByCache,
  brRuntimeState,
  hasBrSubmitted,
  isBrUnlocked,
  loadBrSubmissionStatus,
} from './brRuntime';
import { BusinessRegistrationService } from '../_services/BusinessRegistration.Service';
import {
  findBrExpirySoonTarget,
  findBrResultNotificationTarget,
  getBrSubmissionApprovalId,
  hasBrRenewalInFlight,
} from './brDocumentCore';
import { formatBrLocalIsoDate } from './brPure';

const BR_REMINDER_DISMISSED_KEY = 'emp_br_reminder_dismissed_login';
/** 每次登录仅展示一款弹窗（首页） */
const BR_PROMPT_SHOWN_LOGIN_KEY = 'emp_br_prompt_shown_login';
/** 临期弹窗：按公司按自然日只提醒一次（localStorage，登录/登出不重置） */
const BR_EXPIRY_SOON_DAY_KEY_PREFIX = 'emp_br_expiry_soon_dismissed_day_';
/** 旧版「同登录暂关」key，登录时清理一次即可 */
const BR_EXPIRY_SOON_DISMISSED_LOGIN_LEGACY_KEY = 'emp_br_expiry_soon_dismissed_login';

function normalizeBrApprovalId(value) {
  if (value == null || value === '') {
    return '';
  }
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) {
    return '';
  }
  return String(n);
}

/** Q2-B：只清当前弹出的那一笔 ApprovalId，其它待弹留给下次登录 */
function clearBrResultNotifyPendingInRuntime(approvalId) {
  const idKey = normalizeBrApprovalId(approvalId);
  if (!idKey) {
    return;
  }
  const clearFlag = (item) => {
    if (normalizeBrApprovalId(getBrSubmissionApprovalId(item)) !== idKey) {
      return item;
    }
    return {
      ...item,
      brResultNotifyPending: false,
      BrResultNotifyPending: false,
    };
  };
  brRuntimeState.documentList = (brRuntimeState.documentList || []).map(clearFlag);
  brRuntimeState.nameChangePending = (brRuntimeState.nameChangePending || []).map(clearFlag);
}

/** 执照 + 更名：结果弹窗与临期抑制共用同一候选池 */
function getBrResultNotifyPool(items = brRuntimeState.documentList) {
  return [
    ...(Array.isArray(items) ? items : []),
    ...(Array.isArray(brRuntimeState.nameChangePending) ? brRuntimeState.nameChangePending : []),
  ];
}

function hasReachedKybLockStart() {
  const fromGate = isKybLockPeriodActiveByCache();
  return fromGate != null ? fromGate : false;
}

function getBrCompanyStorageId(user) {
  const id = user?.empCompanyId ?? user?.EmpCompanyId;
  if (id == null || !String(id).trim()) {
    return '';
  }
  return String(id).trim();
}

function getBrExpirySoonDayStorageKey(user) {
  const companyId = getBrCompanyStorageId(user);
  // 必须带公司 ID：避免无 ID 时写成共享 key，导致换公司也不再弹
  if (!companyId) {
    return '';
  }
  return `${BR_EXPIRY_SOON_DAY_KEY_PREFIX}${companyId}`;
}

function getBrLocalTodayIso() {
  return formatBrLocalIsoDate(new Date());
}

function isBrExpirySoonDismissedToday(user) {
  const key = getBrExpirySoonDayStorageKey(user);
  if (!key) {
    return false;
  }
  try {
    return localStorage.getItem(key) === getBrLocalTodayIso();
  } catch (e) {
    return false;
  }
}

/** 标记今日已提醒临期（按 empCompanyId 隔离；无公司 ID 则不写，避免跨公司误伤） */
export function dismissBrExpirySoonForToday(user) {
  const key = getBrExpirySoonDayStorageKey(user);
  if (!key) {
    return;
  }
  try {
    localStorage.setItem(key, getBrLocalTodayIso());
  } catch (e) {
    // ignore
  }
}

export function hasBrPromptShownThisLogin() {
  try {
    return sessionStorage.getItem(BR_PROMPT_SHOWN_LOGIN_KEY) === '1';
  } catch (e) {
    return false;
  }
}

export function markBrPromptShownThisLogin() {
  try {
    sessionStorage.setItem(BR_PROMPT_SHOWN_LOGIN_KEY, '1');
  } catch (e) {
    // ignore
  }
}

/** 过渡期提醒：仅锁定期前；每次登录可弹，同登录点「稍后」暂关 */
export function shouldShowBrReminder(user, route) {
  if (!isBrReminderEnabled()) {
    return false;
  }
  if (hasReachedKybLockStart()) {
    return false;
  }
  if (isBrUnlocked() || hasBrSubmitted()) {
    return false;
  }
  if (route?.path && route.path.indexOf('/businessDocuments') === 0) {
    return false;
  }
  try {
    if (sessionStorage.getItem(BR_REMINDER_DISMISSED_KEY) === '1') {
      return false;
    }
  } catch (e) {
    // ignore
  }
  return true;
}

export function dismissBrReminderForSession() {
  try {
    sessionStorage.setItem(BR_REMINDER_DISMISSED_KEY, '1');
  } catch (e) {
    // ignore
  }
}

/** 登录时仅清 session 弹窗标记；临期「每日一次」存在 localStorage，不在此清除 */
export function resetBrReminderDismissedOnLogin() {
  try {
    sessionStorage.removeItem(BR_REMINDER_DISMISSED_KEY);
    sessionStorage.removeItem(BR_PROMPT_SHOWN_LOGIN_KEY);
    sessionStorage.removeItem(BR_EXPIRY_SOON_DISMISSED_LOGIN_LEGACY_KEY);
  } catch (e) {
    // ignore
  }
}

export function detectBrExpirySoonNotification(user, items = brRuntimeState.documentList) {
  if (!isBrReminderEnabled() || !user) {
    return null;
  }
  const pool = getBrResultNotifyPool(items);
  const resultTarget = findBrResultNotificationTarget(pool);
  const renewalInFlight = hasBrRenewalInFlight(items);
  // 更名 A/R 待弹时也抑制临期（结果弹窗优先）
  if (resultTarget || renewalInFlight) {
    return null;
  }
  // 产品：临期弹窗每天每公司最多一次（不随登入/登出重复弹）
  if (isBrExpirySoonDismissedToday(user)) {
    return null;
  }
  return findBrExpirySoonTarget(items) || null;
}

/**
 * 队列最高一款审批结果（固定优先：驳回 > 通过）。
 * @returns {{ type: 'approved'|'rejected', item: object, approvalId: string }|null}
 */
export function detectBrStatusNotification(user, items = brRuntimeState.documentList) {
  if (!isBrReminderEnabled() || !user) {
    return null;
  }
  const pool = getBrResultNotifyPool(items);
  const target = findBrResultNotificationTarget(pool);
  if (!target || !target.item) {
    return null;
  }
  const approvalId = normalizeBrApprovalId(getBrSubmissionApprovalId(target.item));
  if (!approvalId) {
    return null;
  }
  return {
    type: target.type,
    item: target.item,
    approvalId,
  };
}

/** 关闭即将过期弹窗：记为「今日已提醒」（与弹出时写入幂等） */
export function acknowledgeBrExpirySoonNotification(user) {
  dismissBrExpirySoonForToday(user);
}

/**
 * 关闭审批结果弹窗后确认已读（Q2-B：只标当前这一笔 ApprovalId）。
 * @param {*} user
 * @param {'approved'|'rejected'} [type] 所关闭弹窗种类；无 approvalId 时用来对齐队列里那一笔
 * @param {Array} [items]
 * @param {string|number} [approvalId] 当前弹出/查看的那一笔；有则只 ack 这一笔
 */
export async function acknowledgeBrStatusNotification(
  user,
  type,
  items = brRuntimeState.documentList,
  approvalId,
) {
  const gateRefresh = user
    ? fetchKybAccessGate(user, { force: true })
    : Promise.resolve(null);

  let targetId = normalizeBrApprovalId(approvalId);
  if (!targetId) {
    const pool = getBrResultNotifyPool(items);
    const target = findBrResultNotificationTarget(pool);
    if (target && (!type || target.type === type)) {
      targetId = normalizeBrApprovalId(getBrSubmissionApprovalId(target.item));
    }
  }

  if (!targetId) {
    await gateRefresh;
    return;
  }

  clearBrResultNotifyPendingInRuntime(targetId);
  try {
    await Promise.all([
      BusinessRegistrationService.AcknowledgeResultNotify({
        approvalId: Number(targetId),
      }),
      gateRefresh,
    ]);
  } catch (e) {
    await loadBrSubmissionStatus('hard');
    await gateRefresh;
  }
}
